
import { memo, useEffect, useMemo, useState, useCallback } from 'react';
import {
    Box, Flex, Image, Text, IconButton, Tooltip, Badge,
} from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { remove_from_cart, update_cart_item } from 'redux/actions/productActions';
import WalletHistoryPrice from 'components/Wallet/WalletHistoryPrice';
import cartSvg from '../../assets/shopping-cart.svg';

/* ─────────────────────────────────────────────────────────────
   BRAND TOKENS — match Cart.js
   ───────────────────────────────────────────────────────────── */
const NAVY        = '#1B3B5F';
// const NAVY_DEEP   = '#0F2940';
const CYAN        = '#DCF8F9';
const INK         = '#1A2740';
const MUTED       = '#5A6B7E';
const FAINT       = '#8896AE';
// const SURFACE     = '#FFFFFF';
const SURFACE_ALT = '#F7F9FB';
const BORDER      = '#E8EDF5';
const DANGER      = '#E53E3E';
const DANGER_BG   = '#FEF2F2';

const FONT = "'Bricolage Grotesque', sans-serif";

/* ─────────────────────────────────────────────────────────────
   ICONS
   ───────────────────────────────────────────────────────────── */
const MinusIcon = (props) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M5 12h14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
);
const PlusIcon = (props) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
);
const TrashIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1.5 14a2 2 0 01-2 1.8H8.5a2 2 0 01-2-1.8L5 6"
            stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const PackageIcon = (props) => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"
            stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
        <path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12"
            stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

/* ─────────────────────────────────────────────────────────────
   IMAGE PARSER — handles array, JSON-string-of-array, plain string
   ───────────────────────────────────────────────────────────── */
const parseImage = (raw) => {
    try {
        if (Array.isArray(raw)) return raw[0] ?? null;
        if (typeof raw === 'string') {
            const trimmed = raw.trim();
            if (trimmed.startsWith('[')) {
                const parsed = JSON.parse(trimmed);
                return Array.isArray(parsed) ? parsed[0] : null;
            }
            return trimmed || null;
        }
    } catch {
        return null;
    }
    return null;
};

/* ─────────────────────────────────────────────────────────────
   QUANTITY STEPPER
   ───────────────────────────────────────────────────────────── */
const QuantityStepper = memo(function QuantityStepper({
    value, min = 1, max, onChange, isSyncing,
}) {
    const canDecrement = value > min;
    const canIncrement = max == null || value < max;

    return (
        <Flex
            align="center"
            bg={SURFACE_ALT}
            border="1px solid"
            borderColor={BORDER}
            borderRadius="9px"
            overflow="hidden"
            h="34px"
            opacity={isSyncing ? 0.6 : 1}
            transition="opacity 0.15s ease"
        >
            <IconButton
                aria-label="Decrease quantity"
                icon={<MinusIcon />}
                onClick={() => canDecrement && onChange(value - 1)}
                isDisabled={!canDecrement}
                variant="ghost"
                size="sm"
                h="full"
                minW="32px"
                w="32px"
                borderRadius="0"
                color={canDecrement ? INK : FAINT}
                _hover={{ bg: canDecrement ? CYAN : 'transparent' }}
                _active={{ bg: canDecrement ? CYAN : 'transparent' }}
            />
            <Text
                px="8px"
                fontSize="13px"
                fontWeight="700"
                color={INK}
                fontFamily={FONT}
                minW="44px"
                textAlign="center"
                userSelect="none"
                whiteSpace="nowrap"
                flexShrink={0}
            >
                {value}
            </Text>
            <IconButton
                aria-label="Increase quantity"
                icon={<PlusIcon />}
                onClick={() => canIncrement && onChange(value + 1)}
                isDisabled={!canIncrement}
                variant="ghost"
                size="sm"
                h="full"
                minW="32px"
                w="32px"
                borderRadius="0"
                color={canIncrement ? INK : FAINT}
                _hover={{ bg: canIncrement ? CYAN : 'transparent' }}
                _active={{ bg: canIncrement ? CYAN : 'transparent' }}
            />
        </Flex>
    );
});

/* ─────────────────────────────────────────────────────────────
   READ-ONLY QUANTITY — for checkout context
   ───────────────────────────────────────────────────────────── */
const ReadOnlyQuantity = memo(function ReadOnlyQuantity({ value }) {
    return (
        <Flex
            align="center"
            gap="6px"
            bg={SURFACE_ALT}
            border="1px solid"
            borderColor={BORDER}
            borderRadius="9px"
            px="10px"
            h="32px"
        >
            <Box color={MUTED}><PackageIcon /></Box>
            <Text fontSize="11.5px" color={MUTED} fontWeight="700" fontFamily={FONT}>
                Qty
            </Text>
            <Text fontSize="13px" color={INK} fontWeight="800" fontFamily={FONT}>
                {value}
            </Text>
        </Flex>
    );
});

/* ─────────────────────────────────────────────────────────────
   MAIN — CARTBOX
   ───────────────────────────────────────────────────────────── */
const CartBox = memo(function CartBox({ cartItem, readOnly = false }) {
    const dispatch  = useDispatch();
    const navigate  = useNavigate();

    /* Optimistic local quantity — instantly reflects user input, syncs
       to backend in the background. Reverts to server value on error. */
    const [optimisticQty, setOptimisticQty] = useState(cartItem?.quantity);
    const [isSyncing, setIsSyncing] = useState(false);

    /* Keep local in sync if Redux quantity changes from outside this component
       (e.g. backend refetch, add-to-cart from another tab) */
    useEffect(() => {
        if (!isSyncing) setOptimisticQty(cartItem?.quantity);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cartItem?.quantity]);

    const image = useMemo(() => parseImage(cartItem?.imageurl), [cartItem?.imageurl]);
    const stockMax = Number(cartItem?.cf_stock_available_box) || 99;
    const isOutOfStock = cartItem?.in_stock !== 'Available';

    /* Optimistic quantity update */
    const handleQtyChange = useCallback(
        async (newQty) => {
            if (newQty < 1 || newQty > stockMax) return;
            const previous = optimisticQty;
            setOptimisticQty(newQty);
            setIsSyncing(true);
            try {
                await dispatch(
                    update_cart_item({
                        productId: cartItem?.item_id,
                        qty: newQty,
                    })
                );
            } catch (err) {
                setOptimisticQty(previous);
                toast.error('Could not update quantity. Try again.');
            } finally {
                setIsSyncing(false);
            }
        },
        [dispatch, cartItem?.item_id, optimisticQty, stockMax]
    );

    const handleDelete = useCallback(() => {
        toast.promise(
            dispatch(remove_from_cart({
                productId: cartItem?.item_id,
                qty: cartItem?.quantity,
            })),
            {
                loading: 'Removing…',
                success: 'Removed from cart',
                error: 'Could not remove. Try again.',
            },
            { position: 'top-right' }
        );
    }, [dispatch, cartItem?.item_id, cartItem?.quantity]);

    const handleOpenProduct = useCallback(() => {
        if (!cartItem?.item_id) return;
        navigate(`/dashboard/product/details/${cartItem.item_id}`);
    }, [navigate, cartItem?.item_id]);

    if (!cartItem) return null;

    return (
        <Flex
            gap={{ base: '12px', md: '16px' }}
            align="flex-start"
            position="relative"
            opacity={isOutOfStock ? 0.7 : 1}
        >
            {/* IMAGE — clickable */}
            <Box
                w={{ base: '76px', md: '92px' }}
                h={{ base: '76px', md: '92px' }}
                flexShrink={0}
                bg={SURFACE_ALT}
                border="1px solid"
                borderColor={BORDER}
                borderRadius="10px"
                overflow="hidden"
                cursor="pointer"
                onClick={handleOpenProduct}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleOpenProduct();
                    }
                }}
                role="button"
                tabIndex={0}
                aria-label={`View ${cartItem?.name} details`}
                transition="border-color 0.15s ease, transform 0.15s ease"
                _hover={{ borderColor: NAVY }}
                _focusVisible={{
                    outline: `2px solid ${NAVY}`,
                    outlineOffset: '2px',
                }}
            >
                <Image
                    src={image || cartSvg}
                    alt={cartItem?.name ?? ''}
                    w="full"
                    h="full"
                    objectFit="cover"
                    fallbackSrc={cartSvg}
                />
            </Box>

            {/* MIDDLE — name, vendor, controls */}
            <Flex
                direction="column"
                gap="8px"
                flex="1"
                minW="0"
            >
                {/* Product name */}
                <Text
                    fontSize={{ base: '13px', md: '14px' }}
                    fontWeight="700"
                    color={INK}
                    fontFamily={FONT}
                    lineHeight="1.3"
                    noOfLines={2}
                    cursor="pointer"
                    onClick={handleOpenProduct}
                    _hover={{ color: NAVY }}
                    transition="color 0.15s ease"
                    title={cartItem?.name}
                >
                    {cartItem?.name ?? 'Untitled product'}
                </Text>

                {/* Brand + Vendor meta row */}
<Flex direction="column" gap="2px">
    {cartItem?.brand && (
        <Flex align="center" gap="4px">
            <Text
                fontSize="10.5px"
                color={FAINT}
                fontWeight="700"
                letterSpacing="0.04em"
                minW="42px"
            >
                BRAND
            </Text>
            <Text
                fontSize="11px"
                color={INK}
                fontWeight="700"
                noOfLines={1}
            >
                {cartItem.brand}
            </Text>
        </Flex>
    )}
    {cartItem?.vendor && (
        <Flex align="center" gap="4px">
            <Text
                fontSize="10.5px"
                color={FAINT}
                fontWeight="600"
                letterSpacing="0.04em"
                minW="42px"
            >
                VENDOR
            </Text>
            <Text
                fontSize="11px"
                color={MUTED}
                fontWeight="600"
                noOfLines={1}
            >
                {cartItem.vendor}
            </Text>
        </Flex>
    )}
</Flex>
                {/* Out-of-stock badge */}
                {isOutOfStock && (
                    <Badge
                        bg={DANGER_BG}
                        color={DANGER}
                        fontSize="9.5px"
                        fontWeight="800"
                        px="6px"
                        py="2px"
                        borderRadius="5px"
                        letterSpacing="0.04em"
                        w="max-content"
                        border={`1px solid ${DANGER}30`}
                    >
                        OUT OF STOCK
                    </Badge>
                )}

                {/* Quantity row — visible on mobile inline, hidden on desktop where it's in the right column */}
                <Box display={{ base: 'block', md: 'none' }} mt="2px">
                    {readOnly || isOutOfStock ? (
                        <ReadOnlyQuantity value={optimisticQty} />
                    ) : (
                        <QuantityStepper
                            value={optimisticQty}
                            min={1}
                            max={stockMax}
                            onChange={handleQtyChange}
                            isSyncing={isSyncing}
                        />
                    )}
                </Box>
            </Flex>

            {/* RIGHT — quantity (desktop) + price + delete */}
            <Flex
                direction="column"
                align="flex-end"
                gap="8px"
                flexShrink={0}
                minW={{ md: '120px' }}
            >
                {/* Quantity stepper — desktop only (mobile is inline above) */}
                <Box display={{ base: 'none', md: 'block' }}>
                    {readOnly || isOutOfStock ? (
                        <ReadOnlyQuantity value={optimisticQty} />
                    ) : (
                        <QuantityStepper
                            value={optimisticQty}
                            min={1}
                            max={stockMax}
                            onChange={handleQtyChange}
                            isSyncing={isSyncing}
                        />
                    )}
                </Box>

                {/* Price */}
                <Flex direction="column" align="flex-end" gap="2px">
                    <Text
                        fontSize={{ base: '14px', md: '15px' }}
                        fontWeight="800"
                        color={INK}
                        fontFamily={FONT}
                        lineHeight="1.1"
                    >
                        <WalletHistoryPrice price={cartItem?.total_price} />
                    </Text>
                    {Number(optimisticQty) > 1 && cartItem?.rate && (
                        <Text fontSize="10.5px" color={FAINT} fontWeight="500">
                            <WalletHistoryPrice price={cartItem.rate} /> each
                        </Text>
                    )}
                </Flex>

                {/* Delete button — hidden in checkout context */}
                {!readOnly && (
                    <Tooltip label="Remove from cart" placement="left" hasArrow openDelay={400} fontSize="11px">
                        <IconButton
                            aria-label="Remove from cart"
                            icon={<TrashIcon />}
                            onClick={handleDelete}
                            variant="ghost"
                            size="xs"
                            color={MUTED}
                            h="28px"
                            minW="28px"
                            _hover={{ bg: DANGER_BG, color: DANGER }}
                            _focusVisible={{
                                outline: `2px solid ${DANGER}`,
                                outlineOffset: '2px',
                            }}
                        />
                    </Tooltip>
                )}
            </Flex>
        </Flex>
    );
});

export default CartBox;